import {
    SET_INGREDIENTS,
    SET_SETS,
    SET_PRODUCTS,
    SET_IS_LOADING,
    SET_MENU_SEARCH,
    SET_MENU_CATEGORY,
    SET_MENU_PAGE,
    SET_SELECTED_CATEGORY,
    SET_UNIQUE_CATEGORIES,
} from "./constants"

import { ingredientTypes } from "src/modules/Main/constant"
import { getSets } from "src/api/rest/set"
import { getProducts } from "src/api/rest/product"
import { getIngredients } from "src/api/rest/ingredient"
import {
    getCachedData,
    setCachedData,
    CACHE_KEYS,
    CACHE_DURATIONS,
} from "src/utils/cache"
import { isMenuUsable } from "src/utils/reconcileBasket"

export const setIngredients = items => ({
    type: SET_INGREDIENTS,
    payload: items,
})

const setUniqueCategories = categories => ({
    type: SET_UNIQUE_CATEGORIES,
    payload: categories,
})

// Loads the shop menu (sets, products, ingredients) from the 5-minute cache or,
// on a cache miss / forceRefresh, from Shop-api. Resolves to true only when all
// three lists were loaded as non-empty arrays (see isMenuUsable) — callers rely
// on that before reconciling the basket with the menu (SUSHI-435). Failed or
// empty responses are never cached.
export function fetchMenu(forceRefresh = false) {
    return async dispatch => {
        // Check cache first (unless force refresh is requested)
        if (!forceRefresh) {
            const cachedSets = getCachedData(
                CACHE_KEYS.MENU_SETS,
                CACHE_DURATIONS.MEDIUM
            )
            const cachedProducts = getCachedData(
                CACHE_KEYS.MENU_PRODUCTS,
                CACHE_DURATIONS.MEDIUM
            )
            const cachedIngredients = getCachedData(
                CACHE_KEYS.MENU_INGREDIENTS,
                CACHE_DURATIONS.MEDIUM
            )

            // If all menu data is cached and valid, use it
            if (
                isMenuUsable({
                    sets: cachedSets,
                    products: cachedProducts,
                    ingredients: cachedIngredients,
                })
            ) {
                dispatchMenu(
                    dispatch,
                    cachedSets,
                    cachedProducts,
                    cachedIngredients
                )
                return true // Exit early with cached data
            }
        }

        // Fetch fresh data if cache miss or force refresh
        dispatch(setIsLoading(true))
        try {
            const sets = await getSets()
            const products = await getProducts()
            const ingredients = await getIngredients()

            // An error response (e.g. {status: false}) is not a menu.
            if (
                !Array.isArray(sets) ||
                !Array.isArray(products) ||
                !Array.isArray(ingredients)
            ) {
                throw new Error("Unexpected menu response.")
            }

            const isLoaded = isMenuUsable({ sets, products, ingredients })
            if (isLoaded) {
                // Cache the fresh data
                setCachedData(CACHE_KEYS.MENU_SETS, sets)
                setCachedData(CACHE_KEYS.MENU_PRODUCTS, products)
                setCachedData(CACHE_KEYS.MENU_INGREDIENTS, ingredients)
            }

            dispatchMenu(dispatch, sets, products, ingredients)
            return isLoaded
        } catch (error) {
            console.error("Error while getting menu.", error)
            return false
        } finally {
            dispatch(setIsLoading(false))
        }
    }
}

function dispatchMenu(dispatch, sets, products, ingredients) {
    const filteredProducts = filterProducts(products)
    const filteredIngredients = filterIngredients(ingredients)

    dispatch(setSets(sets))
    dispatch(setProducts(products))
    dispatch(setIngredients(ingredients))
    dispatch(setMenuSearch(""))

    const uniqueCategories = getUniqueCategories([
        ...sets,
        ...filteredProducts,
        ...filteredIngredients,
    ])

    dispatch(setUniqueCategories(uniqueCategories))
}

export const setProducts = items => ({
    type: SET_PRODUCTS,
    payload: items,
})

export const setSets = items => ({
    type: SET_SETS,
    payload: items,
})

export const setIsLoading = isLoading => ({
    type: SET_IS_LOADING,
    payload: isLoading,
})

export const setMenuCategory = category => ({
    type: SET_MENU_CATEGORY,
    payload: category,
})

export const setSelectedCategory = category => ({
    type: SET_SELECTED_CATEGORY,
    payload: category,
})

export const setMenuSearch = search => ({
    type: SET_MENU_SEARCH,
    payload: search,
})

export const setMenuPage = page => ({
    type: SET_MENU_PAGE,
    payload: page,
})

//Helper functions
export function searchItems(items, search) {
    if (!search || !search.trim()) {
        return items
    }

    const lowerCasedSearch = search.toLowerCase()

    return items.filter(item =>
        item.name.toLowerCase().includes(lowerCasedSearch)
    )
}

export function filterProducts(products) {
    return products.filter(product => product.isVisible)
}

export function filterIngredients(ingredients) {
    return ingredients.filter(
        ingredient => ingredient.category !== ingredientTypes.ingredient
    )
}

export function searchCategory(items, category) {
    if (!category || !category.trim()) return items

    return items.filter(item => item.category === category)
}

export function getCategory(oldCategory, newCategory) {
    return oldCategory !== newCategory ? newCategory : ""
}

function getUniqueCategories(menuList) {
    let categories = []
    menuList.forEach(
        ({ category }) =>
            !categories.some(e => e === category) && categories.push(category)
    )
    return categories
}
